<template>
  <div class="dbmng-form">


    <!-- Error display -->
    <div v-if="formErrors.length > 0" class="alert alert-danger">
      <h6><i class="fas fa-exclamation-triangle me-2"></i>Errori di validazione:</h6>
      <ul class="mb-0">
        <li v-for="error in formErrors" :key="error">{{ error }}</li>
      </ul>
    </div>

    <!-- Form dinamico -->
    <form @submit.prevent="$emit('submit', formData)">
      <div v-for="(row, rowIndex) in fieldRows" :key="`row-${rowIndex}`" class="row mb-3">
        <div 
          v-for="(field, fieldIndex) in row" 
          :key="field.fieldName" 
          :class="getColumnClass(field)"
        >



        <!-- Select -->
        <div v-if="field.widget === 'select'">
          <label :for="`field_${field.fieldName}`" class="form-label">
            {{ field.label }}
            <span v-if="!field.nullable" class="text-danger ms-1">*</span>
          </label>
          <select :id="`field_${field.fieldName}`" v-model="formData[field.fieldName]" :class="getFieldClass(field.fieldName, field)"
            :required="!field.nullable">
            <option value="" v-if="field.nullable">
              {{ field.placeholder || 'Seleziona un\'opzione...' }}
            </option>
            <option v-for="option in getSelectOptions(field)" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <div v-if="field.description" class="form-text">{{ field.description }}</div>
        </div>

        <!-- Date -->
        <div v-else-if="field.widget === 'date' || field.type === 'date'">
          <label :for="`field_${field.fieldName}`" class="form-label">
            {{ field.label }}
            <span v-if="!field.nullable" class="text-danger ms-1">*</span>
          </label>
          <input :id="`field_${field.fieldName}`" v-model="formData[field.fieldName]" type="date"
            :class="getFieldClass(field.fieldName, field)" :required="!field.nullable" :min="field.min" :max="field.max">
          <div v-if="field.description" class="form-text">{{ field.description }}</div>
        </div>

        <!-- Textarea -->
        <div v-else-if="field.widget === 'textarea'">
          <label :for="`field_${field.fieldName}`" class="form-label">
            {{ field.label }}
            <span v-if="!field.nullable" class="text-danger ms-1">*</span>
          </label>
          <textarea :id="`field_${field.fieldName}`" v-model="formData[field.fieldName]" :class="getFieldClass(field.fieldName, field)"
            :required="!field.nullable" :placeholder="field.placeholder || `Inserisci ${field.label.toLowerCase()}`"
            :rows="field.rows || 3" :maxlength="field.max_length"></textarea>
          <div v-if="field.description" class="form-text">{{ field.description }}</div>
        </div>

        <!-- Checkbox -->
        <div v-else-if="field.widget === 'checkbox' || field.type === 'boolean'">
          <div class="form-check">
            <input :id="`field_${field.fieldName}`" v-model="formData[field.fieldName]" type="checkbox" class="form-check-input"
              :class="{ 'is-invalid': hasFieldError(field.fieldName, field) }">
            <label :for="`field_${field.fieldName}`" class="form-check-label">
              {{ field.label }}
              <span v-if="!field.nullable" class="text-danger ms-1">*</span>
            </label>
          </div>
          <div v-if="field.description" class="form-text">{{ field.description }}</div>
        </div>

        <!-- Input Text/Email/Number -->
        <div v-else-if="isInputField(field)">
          <label :for="`field_${field.fieldName}`" class="form-label">
            {{ field.label }}
            <span v-if="!field.nullable" class="text-danger ms-1">*</span>
          </label>
          <input :id="`field_${field.fieldName}`" v-model="formData[field.fieldName]" :type="getInputType(field)"
            :class="getFieldClass(field.fieldName, field)" :required="!field.nullable"
            :placeholder="field.placeholder || `Inserisci ${field.label.toLowerCase()}`" :maxlength="field.max_length"
            :min="field.min" :max="field.max" :step="getStep(field)">
          <div v-if="field.description" class="form-text">{{ field.description }}</div>
        </div>

        <!-- Default fallback -->
        <div v-else>
          <label :for="`field_${field.fieldName}`" class="form-label">
            {{ field.label }}
            <span v-if="!field.nullable" class="text-danger ms-1">*</span>
            <small class="text-muted">({{ field.widget || field.type || 'unknown' }})</small>
          </label>
          <input :id="`field_${field.fieldName}`" v-model="formData[field.fieldName]" type="text"
            :class="getFieldClass(field.fieldName, field)" :required="!field.nullable"
            :placeholder="`${field.label} - Widget: ${field.widget || field.type}`">
          <div class="form-text text-warning">
            Tipo campo non riconosciuto: {{ field.widget || field.type }}
          </div>
        </div>

        </div> <!-- Chiusura div colonna -->
      </div> <!-- Chiusura div riga -->

      <!-- Actions -->
      <div class="d-flex justify-content-end gap-2 mt-4" v-if="showActions">
        <button type="button" class="btn btn-secondary" @click="resetForm">
          <i class="fas fa-undo me-2"></i>Reset
        </button>
        <button type="submit" class="btn btn-primary" :disabled="!isFormValid">
          <i class="fas fa-save me-2"></i>{{ submitLabel || 'Salva' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: 'DbmngForm',
  props: {
    // Schema DBMng
    dbmngSchema: {
      type: Object,
      required: true,
      default: () => ({ fields: {} })
    },
    
    // Dati del form (v-model)
    modelValue: {
      type: Object,
      default: () => ({})
    },
    
    // Mostra bottoni azioni
    showActions: {
      type: Boolean,
      default: true
    },
    
    // Label bottone submit
    submitLabel: {
      type: String,
      default: 'Salva'
    },
    
    // Validazione real-time
    validateOnChange: {
      type: Boolean,
      default: true
    }
  },
  
  emits: ['update:modelValue', 'submit', 'validate'],
  
  data() {
    return {
      formData: {},
      formErrors: []
    }
  },
  
  computed: {
    /**
     * Normalizza fields per supportare sia formato array associativo che array
     */
    normalizedFields() {
      if (!this.dbmngSchema || !this.dbmngSchema.fields) {
        return []
      }
      
      const fields = this.dbmngSchema.fields
      
      // Se è un array, ogni oggetto deve avere la proprietà 'field'
      if (Array.isArray(fields)) {
        return fields.map(fieldObj => ({
          ...fieldObj,
          fieldName: fieldObj.field // Nome del campo dall'attributo 'field'
        }))
      }
      
      // Se è un oggetto associativo (formato attuale)
      if (typeof fields === 'object') {
        return Object.keys(fields).map(fieldName => ({
          ...fields[fieldName],
          fieldName: fieldName // Nome del campo dalla chiave
        }))
      }
      
      return []
    },
    
    /**
     * Raggruppa i campi in righe basato sulle colonne Bootstrap
     */
    fieldRows() {
      const rows = []
      let currentRow = []
      let currentRowCols = 0
      
      this.normalizedFields.forEach(field => {
        // Estrai il numero di colonne dal campo col (default 12)
        const colSize = this.getColumnSize(field)
        
        // Se aggiungendo questo campo supereremmo 12 colonne, inizia una nuova riga
        if (currentRowCols + colSize > 12 && currentRow.length > 0) {
          rows.push([...currentRow])
          currentRow = []
          currentRowCols = 0
        }
        
        currentRow.push(field)
        currentRowCols += colSize
        
        // Se la riga è completa (12 colonne), inizia una nuova riga
        if (currentRowCols >= 12) {
          rows.push([...currentRow])
          currentRow = []
          currentRowCols = 0
        }
      })
      
      // Aggiungi l'ultima riga se non vuota
      if (currentRow.length > 0) {
        rows.push(currentRow)
      }
      
      return rows
    },
    
    isFormValid() {
      return this.validateForm().length === 0
    }
  },
  
  watch: {
    // Sincronizza con v-model
    modelValue: {
      handler(newVal) {
        if (newVal && typeof newVal === 'object') {
          // Aggiorna solo se diverso (deep compare)
          const isDifferent = JSON.stringify(newVal) !== JSON.stringify(this.formData)
          if (isDifferent) {
            this.formData = { ...newVal }
          }
        }
      },
      immediate: true,
      deep: true
    },
    
    // Emette cambiamenti verso parent
    formData: {
      handler(newVal) {
        // Normalizza i valori prima di emetterli
        const normalizedData = this.normalizeFormData(newVal)
        this.$emit('update:modelValue', { ...normalizedData })
        if (this.validateOnChange) {
          this.formErrors = this.validateForm()
          this.$emit('validate', { valid: this.isFormValid, errors: this.formErrors })
        }
      },
      deep: true
    },
    
    // Reinizializza form quando cambia schema
    dbmngSchema: {
      handler() {
        this.initializeForm()
      },
      immediate: true,
      deep: true
    }
  },
  
  methods: {
    /**
     * Restituisce la classe CSS per la colonna Bootstrap
     */
    getColumnClass(field) {
      // Usa il campo 'col' se presente, altrimenti default a 'col-12'
      const colClass = field.col || 'col-12'
      
      // Aggiunge eventuali classi CSS aggiuntive
      const additionalClasses = field.class || ''
      
      return `${colClass} ${additionalClasses}`.trim()
    },
    
    /**
     * Estrae il numero di colonne dal campo 'col'
     */
    getColumnSize(field) {
      if (!field.col) return 12 // Default a 12 colonne
      
      // Cerca pattern come 'col-6', 'col-md-4', etc.
      const colMatch = field.col.match(/col(?:-\w+)?-(\d+)/)
      if (colMatch) {
        return parseInt(colMatch[1])
      }
      
      // Se non trova pattern specifico, assume full width
      return 12
    },
    
    /**
     * Inizializza form con valori default
     */
    initializeForm() {
      const newData = {}
      
      this.normalizedFields.forEach(field => {
        const fieldName = field.fieldName
        
        // Usa valore esistente se presente
        if (this.modelValue && this.modelValue[fieldName] !== undefined) {
          newData[fieldName] = this.modelValue[fieldName]
        }
        // Altrimenti usa valore di default
        else if (field.default !== undefined) {
          newData[fieldName] = field.default
        }
        // Valore vuoto basato sul tipo
        else {
          newData[fieldName] = this.getEmptyValue(field)
        }
      })
      
      this.formData = newData
    },
    
    /**
     * Determina se un campo è un input standard
     */
    isInputField(field) {
      const inputTypes = ['varchar', 'text', 'int', 'float', 'email', 'number']
      const inputWidgets = ['input', 'email', 'number']
      
      return (
        inputTypes.includes(field.type) || 
        inputWidgets.includes(field.widget) ||
        (!field.widget && inputTypes.includes(field.type))
      )
    },
    
    /**
     * Determina tipo input HTML
     */
    getInputType(field) {
      if (field.widget === 'email' || field.type === 'email') return 'email'
      if (field.widget === 'number' || field.type === 'int' || field.type === 'float') return 'number'
      if (field.widget === 'password') return 'password'
      return 'text'
    },
    
    /**
     * Determina step per input numerici
     */
    getStep(field) {
      if (field.type === 'float') return '0.01'
      if (field.type === 'int') return '1'
      return null
    },
    
    /**
     * Ottiene opzioni per select
     */
    getSelectOptions(field) {
      if (field.options && Array.isArray(field.options)) {
        return field.options.map(opt => {
          if (typeof opt === 'object') {
            return { value: opt.value, label: opt.label }
          } else {
            return { value: opt, label: opt }
          }
        })
      }
      
      if (field.voc_val && Array.isArray(field.voc_val)) {
        return field.voc_val.map(item => {
          if (typeof item === 'object') {
            const key = Object.keys(item)[0]
            return { value: key, label: item[key] }
          }
          return { value: item, label: item }
        })
      }
      
      return []
    },
    
    /**
     * Normalizza i dati del form convertendo stringhe vuote in null per date/numeri
     */
    normalizeFormData(formData) {
      const normalized = { ...formData }
      
      this.normalizedFields.forEach(field => {
        const fieldName = field.fieldName
        const value = normalized[fieldName]
        
        // Converte stringa vuota in null per date e numeri
        if (value === '') {
          if (field.type === 'date' || field.widget === 'date' || 
              field.type === 'int' || field.type === 'float') {
            normalized[fieldName] = null
          }
        }
      })
      
      return normalized
    },
    
    /**
     * Valore vuoto per tipo di campo
     */
    getEmptyValue(field) {
      if (field.type === 'boolean' || field.widget === 'checkbox') return false
      if (field.type === 'int' || field.type === 'float') return null
      if (field.type === 'date' || field.widget === 'date') return null
      return ''
    },
    
    /**
     * Classe CSS per campo
     */
    getFieldClass(fieldName, field) {
      const baseClass = field.widget === 'select' ? 'form-select' : 'form-control'
      return {
        [baseClass]: true,
        'is-invalid': this.hasFieldError(fieldName, field)
      }
    },
    
    /**
     * Verifica se campo ha errori
     */
    hasFieldError(fieldName, field) {
      return this.formErrors.some(error => error.includes(field.label))
    },
    
    /**
     * Valida form completo
     */
    validateForm() {
      const errors = []
      
      this.normalizedFields.forEach(field => {
        const fieldName = field.fieldName
        const value = this.formData[fieldName]
        
        // Campo obbligatorio
        if (!field.nullable && this.isEmpty(value)) {
          errors.push(`${field.label} è obbligatorio`)
        }
        
        // Validazione tipo email
        if (value && (field.type === 'email' || field.widget === 'email') && !this.isValidEmail(value)) {
          errors.push(`${field.label} deve essere un email valido`)
        }
        
        // Validazione lunghezza massima
        if (value && field.max_length && String(value).length > field.max_length) {
          errors.push(`${field.label} non può superare ${field.max_length} caratteri`)
        }
        
        // Validazione numeri
        if (value !== null && value !== '' && (field.type === 'int' || field.type === 'float')) {
          if (isNaN(value)) {
            errors.push(`${field.label} deve essere un numero`)
          } else {
            const num = parseFloat(value)
            if (field.min !== undefined && num < field.min) {
              errors.push(`${field.label} deve essere almeno ${field.min}`)
            }
            if (field.max !== undefined && num > field.max) {
              errors.push(`${field.label} non può essere maggiore di ${field.max}`)
            }
          }
        }
        
        // Validazione regexp custom
        if (value && field.validator && field.validator.regexp) {
          const re = new RegExp(field.validator.regexp)
          if (!re.test(value)) {
            errors.push(field.validator.message || `${field.label} non rispetta il formato richiesto`)
          }
        }
      })
      
      return errors
    },
    
    /**
     * Verifica se valore è vuoto
     */
    isEmpty(value) {
      return value === null || value === undefined || value === ''
    },
    
    /**
     * Valida email
     */
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    
    /**
     * Reset form
     */
    resetForm() {
      this.initializeForm()
      this.formErrors = []
    }
  }
}
</script>

<style scoped>
.dbmng-form {
  max-width: 100%;
}

.form-label {
  font-weight: 500;
}

.text-danger {
  font-weight: bold;
}

.form-text {
  font-size: 0.875rem;
}

.is-invalid {
  border-color: #dc3545;
}

.btn {
  border-radius: 6px;
}

.gap-2 {
  gap: 0.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .d-flex.justify-content-end {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}
</style>